import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import api from "../lib/api";
import { Loader2, Clock, ChevronLeft, CheckCircle2 } from "lucide-react";
import dayjs from "dayjs";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setPost(null);
    setError(false);
    window.scrollTo(0, 0);
    api.get(`/blog/${slug}`)
      .then((r) => {
        setPost(r.data);
        document.title = `${r.data.title} | Europcar UAE Blog`;
        let meta = document.querySelector('meta[name="description"]');
        if (!meta) {
          meta = document.createElement("meta");
          meta.name = "description";
          document.head.appendChild(meta);
        }
        meta.content = r.data.excerpt;
      })
      .catch(() => setError(true));
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    const origin = window.location.origin;
    const href = `${origin}/blog/${post.slug}`;
    const schemas = [
      {
        "@context": "https://schema.org", "@type": "Article",
        headline: post.title, description: post.excerpt, image: [post.image],
        datePublished: post.published, dateModified: post.updated_at || post.published,
        author: { "@type": "Organization", name: post.author },
        publisher: { "@type": "Organization", name: "Europcar Dubai & Northern Emirates", logo: { "@type": "ImageObject", url: `${origin}/favicon.ico` } },
        mainEntityOfPage: { "@type": "WebPage", "@id": href },
      },
      {
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: origin },
          { "@type": "ListItem", position: 2, name: "Blog", item: `${origin}/blog` },
          { "@type": "ListItem", position: 3, name: post.title, item: href },
        ],
      },
    ];
    if (post.faqs?.length) {
      schemas.push({
        "@context": "https://schema.org", "@type": "FAQPage",
        mainEntity: post.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      });
    }
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "article-jsonld";
    script.textContent = JSON.stringify(schemas);
    document.head.appendChild(script);
    return () => document.getElementById("article-jsonld")?.remove();
  }, [post]);

  const shell = (content) => (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      <Header />
      {content}
      <Footer />
    </div>
  );

  if (error)
    return shell(
      <main className="flex-1 flex items-center justify-center text-sm text-[#57685e]">
        Article not found. <Link to="/blog" className="underline ml-1 text-[#009900]">Back to blog</Link>
      </main>
    );

  if (!post)
    return shell(<main className="flex-1 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#009900]" /></main>);

  return shell(
    <main className="flex-1" data-testid="blog-post-page">
      <section className="relative h-[380px] sm:h-[440px] overflow-hidden">
        <img src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1E17]/90 via-[#0F1E17]/40 to-transparent" />
        <div className="absolute bottom-0 inset-x-0">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-10">
            <span className="inline-block bg-[#FFDC00] text-[#0F1E17] text-[10px] uppercase tracking-widest font-semibold px-3 py-1 rounded-full mb-4">{post.category}</span>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight" data-testid="blog-post-title">{post.title}</h1>
            <div className="flex items-center gap-4 mt-4 text-xs text-white/70">
              <span>{post.author}</span>
              <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {post.reading_time} min read</span>
              <span>{dayjs(post.published).format("DD MMMM YYYY")}</span>
            </div>
          </div>
        </div>
      </section>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <Link to="/blog" className="inline-flex items-center gap-1 text-xs uppercase tracking-wide font-semibold text-[#009900] hover:underline mb-8" data-testid="blog-back-link">
          <ChevronLeft className="w-4 h-4" /> All articles
        </Link>
        <p className="text-lg text-[#144733] leading-relaxed font-medium mb-10">{post.excerpt}</p>

        {post.sections.map((s, idx) => (
          <div key={s.heading}>
            <section className="mb-10">
              <h2 className="heading-upper text-xl text-[#144733] mb-4 promo-stripe">{s.heading}</h2>
              {s.paragraphs.map((p, j) => (
                <p key={`${s.heading}-p${j}`} className="text-base text-[#334139] leading-relaxed mb-4">{p}</p>
              ))}
              {s.bullets && (
                <ul className="space-y-2 mt-4">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex gap-2.5 text-sm text-[#334139]">
                      <CheckCircle2 className="w-4 h-4 text-[#009900] mt-0.5 shrink-0" /> {b}
                    </li>
                  ))}
                </ul>
              )}
            </section>
            {idx === 1 && (
              <div className="bg-[#f0f9f0] border border-[#009900]/20 rounded-2xl p-5 mb-10 flex flex-wrap items-center justify-between gap-3" data-testid="blog-mid-cta">
                <p className="text-sm text-[#144733]">{post.cta?.text || "Prices shown sitewide include VAT — see live rates for your dates."}</p>
                <Link to={post.cta?.to || "/rent-a-car"} className="btn-cta rounded px-5 py-2.5 text-xs shrink-0">{post.cta?.label || "Check live prices"}</Link>
              </div>
            )}
          </div>
        ))}

        <div className="promo-stripe bg-white rounded-r-2xl border border-l-0 border-[#E2E8F0] p-6 my-10" data-testid="blog-takeaways">
          <h3 className="font-bold text-[#0F1E17] uppercase tracking-wide text-sm mb-4">Key takeaways</h3>
          <ul className="space-y-2.5">
            {post.takeaways.map((t) => (
              <li key={t} className="flex gap-2.5 text-sm text-[#334139] font-medium">
                <span className="text-[#009900] font-bold">→</span> {t}
              </li>
            ))}
          </ul>
        </div>

        {post.faqs?.length > 0 && (
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 my-10" data-testid="blog-faq-block">
            <h3 className="font-bold text-[#0F1E17] uppercase tracking-wide text-sm mb-4">Quick answers</h3>
            <div className="divide-y divide-[#f1f3f1]">
              {post.faqs.map((f) => (
                <details key={f.q} className="py-3 group">
                  <summary className="text-sm font-semibold text-[#0F1E17] cursor-pointer list-none flex justify-between items-center">{f.q}<span className="text-[#009900] group-open:rotate-45 transition-transform text-lg leading-none">+</span></summary>
                  <p className="text-sm text-[#57685e] mt-2 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        )}

        <div className="bg-[#144733] rounded-2xl p-8 sm:p-10 text-center relative overflow-hidden" data-testid="blog-book-cta">
          <div className="absolute -left-16 -bottom-16 w-64 h-64 rounded-full bg-[#009900]/40 blur-3xl" />
          <h3 className="heading-upper text-xl sm:text-2xl text-white relative">Ready for the road?</h3>
          <p className="text-white/75 text-sm mt-2 relative">Short-term from AED 56/day · Monthly rentals from AED 1,098 — all incl. VAT</p>
          <Link to="/" className="btn-cta rounded-md px-8 py-3.5 text-sm inline-block mt-6 relative">Book now</Link>
        </div>
      </article>

      {post.related?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
          <h2 className="heading-upper text-xl text-[#144733] promo-stripe mb-8">Keep reading</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {post.related.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`}
                className="group bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden card-lift"
                data-testid="related-article-card">
                <div className="h-40 overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-5">
                  <span className="text-[10px] uppercase tracking-widest font-semibold text-[#009900]">{p.category}</span>
                  <h3 className="font-bold text-sm text-[#0F1E17] mt-1 leading-snug">{p.title}</h3>
                  <span className="text-[11px] text-[#8a978f] flex items-center gap-1 mt-2"><Clock className="w-3 h-3" /> {p.reading_time} min read</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
